import { useParams } from 'react-router-dom'
import { getForecastPowerDataFromStore } from 'src/Libs/system-data-handler/utils/helperFunctions'
import { useFetchPocPerformantChartValuesManual } from 'src/Libs/system-charts/default-charts/hooks/use-fetch-poc-performant-chart-values'
import { HistoryChartData } from 'src/Libs/system-data-handler/utils/chartFunctions'
import { addMinutes, endOfHour, format, isAfter, startOfHour } from '@e3dc-react/shell/Libs/date'
import { round } from 'lodash'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { PowermeterHardwareDataModel } from 'src/State/Storage/StorageModel'
import { UnitTypes } from 'src/Libs/types'
import { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { CustomLayout, getTraceLabel, getTraceName, LabelConfig, useChartDateBarContext } from 'src/Libs/chart-kit'
import { Resolution, ResolutionTypes } from 'src/Libs/chart-kit/shared/types'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'

export type HistoryChartCsvData = Record<string, number | string | boolean>
export interface HistoryChartCsvHeaders {
  key: string
  label: string
}
export interface HistoryExportDataParams {
  csvData: HistoryChartCsvData[]
  csvHeaders: HistoryChartCsvHeaders[]
}

interface ReturnType {
  getHistoryExportData: (resolution: Resolution, t: TFunction) => Promise<HistoryExportDataParams>
  isFetching: boolean
}

/**
 * Hook provides logic to get history export data for a given resolution
 */
const useHistoryExportData = (customLayout?: CustomLayout): ReturnType => {
  const { t } = useCustomTranslation()
  const { from, to, view } = useChartDateBarContext()
  const { storageId } = useParams<{ installationId: string; storageId: string }>()

  const forecastPower = getForecastPowerDataFromStore(from, to, storageId ?? '')

  const { handleFetchingPocPerformanceChartValues, isFetching } = useFetchPocPerformantChartValuesManual(
    storageId ?? '',
    from,
    to,
    view,
    forecastPower
  )

  const { data: wallboxes } = useWallboxHardwareByStorage()
  const powermeterHardware = useTypedSelector(store => store.storage.powermeterHardware)

  const traceNames = useMemo(() => {
    if (!customLayout) return {}
    return customLayout.traces.reduce((prev: Record<string, string>, customTrace) => {
      if (customTrace) {
        prev[customTrace.alternateKey ?? customTrace.key] = getTraceName(t, customTrace, wallboxes, powermeterHardware)
      }

      return prev
    }, {})
  }, [customLayout, t, wallboxes, powermeterHardware])

  /**
   * Function which generates csv data and headers based on history data to export the data based on the given resolution
   */
  const getHistoryExportData = async (resolution: Resolution, t: TFunction): Promise<HistoryExportDataParams> => {
    const isDayViewOrHourResolution = view === 'day' || resolution === ResolutionTypes.Hour || resolution === ResolutionTypes.FifteenMinutes

    const historyData = await handleFetchingPocPerformanceChartValues(resolution)

    const filteredData = historyData.filter(d => {
      const now = new Date()
      const currentHour = endOfHour(now)
      const currentQuarterHour = addMinutes(startOfHour(now), Math.floor(now.getMinutes() / 15) * 15)

      if (resolution === ResolutionTypes.FifteenMinutes) {
        return !isAfter(d.ts, currentQuarterHour)
      }
      return !isAfter(d.ts, currentHour)
    })

    const labelConfig = createHistoryChartLabelConfig(customLayout, traceNames, t, wallboxes, powermeterHardware, isDayViewOrHourResolution)
    const csvData = filteredData.map(data => mapHistoryDataToCsv(data, isDayViewOrHourResolution, t))
    const csvHeaders = Object.keys(labelConfig).map(key => ({ key, label: getTraceLabel(key, labelConfig, t) }))

    return { csvData, csvHeaders }
  }

  return { getHistoryExportData, isFetching }
}

export default useHistoryExportData

const mapHistoryDataToCsv = (data: HistoryChartData, isDayViewOrHourResolution: boolean, t: TFunction): HistoryChartCsvData => {
  const csvObject: Record<string, number | string | boolean> = {}

  Object.keys(data).forEach(key => {
    const keyValue = data[key] as number

    if (!isDayViewOrHourResolution && key === 'ASOC') return

    if (key === 'ts') {
      const formattedDate = isDayViewOrHourResolution
        ? format(data.ts, t('common.format.date-fns-date-time'))
        : format(data.ts, t('common.format.date-fns-date'))

      csvObject[key] = formattedDate
    } else if (typeof keyValue === 'number') {
      csvObject[key] = round(keyValue)
    } else {
      csvObject[key] = keyValue
    }
  })

  return csvObject
}

/**
 * Function to create label config used for generating the column names/headers in the csv export
 */
const createHistoryChartLabelConfig = (
  chartLayout: CustomLayout | undefined,
  traceNames: Record<string, string>,
  t: TFunction,
  wallboxes?: WallboxHardwareModel[],
  powermeterHardware?: PowermeterHardwareDataModel,
  isDayViewOrHourResolution?: boolean
): LabelConfig<HistoryChartCsvData> => {
  const labelConfig: LabelConfig<HistoryChartCsvData> = {}

  labelConfig.ts = {
    translationKey: 'systemDataHandler.chartHeader.ts',
  }

  const displayedKeys = chartLayout?.traces.map(trace => trace.alternateKey ?? trace.key).filter(key => key !== 'ForecastPower') || []

  displayedKeys.forEach(key => {
    const trace = chartLayout?.traces.find(t => (t.alternateKey ?? t.key) === key)
    const unit = trace?.unit ?? chartLayout?.unit

    if (!isDayViewOrHourResolution && key === 'ASOC') return

    if (trace) {
      const traceName = getTraceName(t, trace, wallboxes, powermeterHardware)

      labelConfig[key] = {
        translationKey: traceName,
        unit: unit as UnitTypes,
      }
    } else if (traceNames[key]) {
      labelConfig[key] = {
        translationKey: traceNames[key],
        unit: unit as UnitTypes,
      }
    }
  })

  return labelConfig
}
